
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/auth';
import { toast } from 'sonner';

export const useAvatar = () => {
  const { user, profile, updateProfile } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(profile?.avatar_url || null);

  // Sync the avatarUrl state with profile changes
  useEffect(() => {
    setAvatarUrl(profile?.avatar_url || null);
  }, [profile?.avatar_url]);

  // Check for Google avatar and sync it if profile doesn't have an avatar
  useEffect(() => {
    const syncGoogleAvatar = async () => {
      if (
        user && 
        user.user_metadata?.avatar_url && 
        (!profile?.avatar_url || profile.avatar_url !== user.user_metadata.avatar_url)
      ) {
        console.log('[useAvatar] Syncing Google avatar to profile:', user.user_metadata.avatar_url);
        try {
          await updateProfile({ avatar_url: user.user_metadata.avatar_url });
          setAvatarUrl(user.user_metadata.avatar_url);
        } catch (error) {
          console.error('[useAvatar] Failed to sync Google avatar:', error);
        }
      }
    };

    syncGoogleAvatar();
  }, [user, profile, updateProfile]);

  const uploadAvatar = async (file: File): Promise<string | null> => {
    if (!user) return null;
    
    try {
      setIsUploading(true);
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;
      
      // Upload the file to Supabase storage
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file);
      
      if (uploadError) {
        throw uploadError;
      }
      
      // Get public URL
      const { data } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);
      
      const newAvatarUrl = data.publicUrl;
      
      // Update user profile with avatar URL
      if (newAvatarUrl) {
        await updateProfile({ avatar_url: newAvatarUrl });
        setAvatarUrl(newAvatarUrl);
        toast.success('Profile picture updated successfully');
      }
      
      return newAvatarUrl;
    } catch (error: any) {
      toast.error(`Error uploading avatar: ${error.message}`);
      console.error('Error uploading avatar:', error);
      return null;
    } finally {
      setIsUploading(false);
    }
  };
  
  const removeAvatar = async (): Promise<boolean> => {
    if (!user || !profile?.avatar_url) return false;
    
    try {
      setIsUploading(true);
      
      // Extract file path from URL
      const urlParts = profile.avatar_url.split('/');
      const filePath = `${urlParts[urlParts.length - 2]}/${urlParts[urlParts.length - 1]}`;
      
      // Delete file from storage
      const { error: deleteError } = await supabase.storage
        .from('avatars')
        .remove([filePath]);
      
      if (deleteError) {
        throw deleteError;
      }
      
      // Update profile to remove avatar URL
      await updateProfile({ avatar_url: null });
      setAvatarUrl(null);
      toast.success('Profile picture removed');
      return true;
    } catch (error: any) {
      toast.error(`Error removing avatar: ${error.message}`);
      console.error('Error removing avatar:', error);
      return false;
    } finally {
      setIsUploading(false);
    }
  };
  
  return {
    uploadAvatar,
    removeAvatar,
    isUploading,
    avatarUrl
  };
};
